import { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, Users, DollarSign, Atom, Building } from 'lucide-react';
import { useKpiFundingTrendsV1 } from '@/hooks/database/useKpiFundingTrendsV1';
import { useKpiFemaleFoundersV1 } from '@/hooks/database/useKpiFemaleFoundersV1';
import { useKpiDeeptechStartupsV1 } from '@/hooks/database/useKpiDeeptechStartupsV1';
import { useKpiScaleupJobsImpactV1 } from '@/hooks/database/useKpiScaleupJobsImpactV1';
import { useKpiTaxRevenueTrendsV1 } from '@/hooks/database/useKpiTaxRevenueTrendsV1';
import { useKpiJobsCreatedTrendsV1 } from '@/hooks/database/useKpiJobsCreatedTrendsV1';
import { logger } from '@/utils/errorLogger';
import { swedenStats2025 } from '@/data/statistics2025';

const KeyInsightsSectionSv = () => {
  // Fetch all KPIs from database
  const { data: fundingData, isError: fundingError, error: fundingErrorObj } = useKpiFundingTrendsV1();
  const { data: femaleFoundersData, isError: femaleFoundersError, error: femaleFoundersErrorObj } = useKpiFemaleFoundersV1();
  const { data: deeptechData, isError: deeptechError, error: deeptechErrorObj } = useKpiDeeptechStartupsV1();
  const { data: scaleupJobsData, isError: scaleupJobsError, error: scaleupJobsErrorObj } = useKpiScaleupJobsImpactV1();
  const { data: taxRevenueData, isError: taxRevenueError, error: taxRevenueErrorObj } = useKpiTaxRevenueTrendsV1();
  const { data: jobsCreatedData, isError: jobsCreatedError, error: jobsCreatedErrorObj } = useKpiJobsCreatedTrendsV1();

  // Error logging - split into separate useEffects to prevent duplicate logging
  useEffect(() => {
    if (fundingError) {
      logger.error('KeyInsightsSection', 'Failed to fetch funding trends KPI', fundingErrorObj as Error, {
        queryKey: 'kpi-funding-trends-v1'
      });
    }
  }, [fundingError, fundingErrorObj]);

  useEffect(() => {
    if (femaleFoundersError) {
      logger.error('KeyInsightsSection', 'Failed to fetch female founders KPI', femaleFoundersErrorObj as Error, {
        queryKey: 'kpi-female-founders-v1'
      });
    }
  }, [femaleFoundersError, femaleFoundersErrorObj]);

  useEffect(() => {
    if (deeptechError) {
      logger.error('KeyInsightsSection', 'Failed to fetch deeptech startups KPI', deeptechErrorObj as Error, {
        queryKey: 'kpi-deeptech-startups-v1'
      });
    }
  }, [deeptechError, deeptechErrorObj]);

  useEffect(() => {
    if (scaleupJobsError) {
      logger.error('KeyInsightsSection', 'Failed to fetch scaleup jobs impact KPI', scaleupJobsErrorObj as Error, {
        queryKey: 'kpi-scaleup-jobs-impact-v1'
      });
    }
  }, [scaleupJobsError, scaleupJobsErrorObj]);

  useEffect(() => {
    if (taxRevenueError) {
      logger.error('KeyInsightsSection', 'Failed to fetch tax revenue trends KPI', taxRevenueErrorObj as Error, {
        queryKey: 'kpi-tax-revenue-trends-v1'
      });
    }
  }, [taxRevenueError, taxRevenueErrorObj]);

  useEffect(() => {
    if (jobsCreatedError) {
      logger.error('KeyInsightsSection', 'Failed to fetch jobs created trends KPI', jobsCreatedErrorObj as Error, {
        queryKey: 'kpi-jobs-created-trends-v1'
      });
    }
  }, [jobsCreatedError, jobsCreatedErrorObj]);

  const percentage1 = scaleupJobsData?.scaleup_company_percentage ?? swedenStats2025.keyInsights.scaleupCompanyPercentage;
  const percentage2 = scaleupJobsData?.scaleup_job_percentage ?? swedenStats2025.keyInsights.scaleupJobPercentage;

  return (
  <section className="py-24 bg-black">
    <div className="w-full px-8 md:px-16 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white">Nyckelinsikter</h2>
          <p className="text-xl text-blue-200 max-w-3xl mx-auto">
            Datadrivna insikter som formar Sveriges startup-landskap och informerar politiska beslut
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Funding Trends */}
          <Card className="bg-slate-900/50 border border-blue-500/20 shadow-xl hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300 animate-on-scroll backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-6">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-3xl font-bold text-white">€{fundingData?.total_funding_billions_eur ?? swedenStats2025.keyInsights.totalFunding2025}B</span>
                <span className="text-sm font-semibold text-green-400">{fundingData?.funding_change_percentage ? `${fundingData.funding_change_percentage > 0 ? '+' : ''}${fundingData.funding_change_percentage}%` : swedenStats2025.keyInsights.fundingChange}</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Finansieringstrender</h3>
              <p className="text-blue-200 text-sm">Total startup-finansiering 2025, visar tillväxt jämfört med föregående år.</p>
            </CardContent>
          </Card>
          {/* Scaleup Jobs */}
          <Card className="bg-slate-900/50 border border-cyan-500/20 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-300 animate-on-scroll backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-cyan-400" />
              </div>
              <div className="mb-3">
                <span className="text-2xl font-bold text-white">{percentage1}% genererar {percentage2}% av alla jobb</span>
              </div>
              <p className="text-blue-200 text-sm">{percentage1}% av nordiska SME är scale-ups, som skapar {percentage2}% av alla jobb för SME</p>
            </CardContent>
          </Card>
          {/* Tax Revenue */}
          <Card className="bg-slate-900/50 border border-indigo-500/20 shadow-xl hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-300 animate-on-scroll backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center mb-6">
                <DollarSign className="w-6 h-6 text-indigo-400" />
              </div>
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-3xl font-bold text-white">€{taxRevenueData?.tax_revenue_millions_eur ?? swedenStats2025.keyInsights.taxRevenue}M</span>
                <span className={`text-sm font-semibold ${(taxRevenueData?.tax_revenue_change_percentage ?? -7) < 0 ? 'text-red-400' : 'text-green-400'}`}>{taxRevenueData?.tax_revenue_change_percentage ? `${taxRevenueData.tax_revenue_change_percentage > 0 ? '+' : ''}${taxRevenueData.tax_revenue_change_percentage}%` : swedenStats2025.keyInsights.taxRevenueChange}</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Skatteintäkter</h3>
              <p className="text-blue-200 text-sm">Skatteintäkter genererade av startup-ekosystemet de senaste 10 åren, bidrar till den nationella ekonomin</p>
            </CardContent>
          </Card>
          {/* Female Founders */}
          <Card className="bg-slate-900/50 border border-sky-500/20 shadow-xl hover:shadow-2xl hover:shadow-sky-500/10 transition-all duration-300 animate-on-scroll backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="w-12 h-12 bg-sky-500/20 rounded-lg flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-sky-400" />
              </div>
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-3xl font-bold text-white">{femaleFoundersData?.female_founders_percentage ?? swedenStats2025.keyInsights.femaleFoundersPercentage}%</span>
                <span className={`text-sm font-semibold ${(femaleFoundersData?.female_founders_change_percentage ?? 2) < 0 ? 'text-red-400' : 'text-green-400'}`}>{femaleFoundersData?.female_founders_change_percentage ? `${femaleFoundersData.female_founders_change_percentage > 0 ? '+' : ''}${femaleFoundersData.female_founders_change_percentage}%` : swedenStats2025.keyInsights.femaleFoundersChange}</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Kvinnliga grundare</h3>
              <p className="text-blue-200 text-sm">Andel startups med kvinnliga grundare, över EU-genomsnittet</p>
            </CardContent>
          </Card>
          {/* Jobs Created */}
          <Card className="bg-slate-900/50 border border-blue-500/20 shadow-xl hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300 animate-on-scroll backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-6">
                <Building className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-3xl font-bold text-white">{((jobsCreatedData?.jobs_created ?? swedenStats2025.keyInsights.jobsCreated) / 1000).toFixed(0)}K</span>
                <span className={`text-sm font-semibold ${(jobsCreatedData?.jobs_created_change_percentage ?? -2) < 0 ? 'text-red-400' : 'text-green-400'}`}>{jobsCreatedData?.jobs_created_change_percentage ? `${jobsCreatedData.jobs_created_change_percentage > 0 ? '+' : ''}${jobsCreatedData.jobs_created_change_percentage}%` : swedenStats2025.keyInsights.jobsCreatedChange}</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Skapade jobb</h3>
              <p className="text-blue-200 text-sm">Nya jobb skapade av startup-ekosystemet det senaste året</p>
            </CardContent>
          </Card>
          {/* Deeptech Startups */}
          <Card className="bg-slate-900/50 border border-violet-500/20 shadow-xl hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-300 animate-on-scroll backdrop-blur-sm">
            <CardContent className="p-8">
              <div className="w-12 h-12 bg-violet-500/20 rounded-lg flex items-center justify-center mb-6">
                <Atom className="w-6 h-6 text-violet-400" />
              </div>
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-3xl font-bold text-white">{deeptechData?.deeptech_count ?? swedenStats2025.keyInsights.deeptechStartups}</span>
                <span className="text-sm font-semibold text-green-400">{deeptechData?.deeptech_growth_percentage ? `+${deeptechData.deeptech_growth_percentage}%` : swedenStats2025.keyInsights.deeptechGrowth}</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Deeptech-startups</h3>
              <p className="text-blue-200 text-sm">Aktiva startups registrerade i Sverige för närvarande</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  </section>
  );
};

export default KeyInsightsSectionSv;
